import { useCallback, useEffect, useRef, useState } from 'react';

type Track = 'waves' | 'gulls' | 'piano';
type Engine = {
  context: AudioContext; master: GainNode; buffers: Partial<Record<Track, AudioBuffer>>;
  loops: Map<Track, AudioBufferSourceNode>; timer: ReturnType<typeof setTimeout> | null;
  loading: Promise<void> | null; wants: boolean;
};

export function useSoundscape() {
  const engine = useRef<Engine | null>(null);
  const settings = useRef({ muted: false, volume: .4 });
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [volume, setVolumeState] = useState(.4);
  const [message, setMessage] = useState<string | null>(null);

  const applyVolume = useCallback(() => {
    const e = engine.current;
    if (e && e.context.state !== 'closed') e.master.gain.setTargetAtTime(settings.current.muted ? 0 : settings.current.volume, e.context.currentTime, .12);
  }, []);

  const start = useCallback(() => {
    let e = engine.current;
    try {
      if (!e || e.context.state === 'closed') {
        const context = new AudioContext(), master = context.createGain();
        master.gain.value = 0; master.connect(context.destination);
        e = { context, master, buffers: {}, loops: new Map(), timer: null, loading: null, wants: true };
        engine.current = e;
      }
      e.wants = true;
      const own = e;
      // Resume inside the user's gesture; loading the buffers can finish afterwards.
      const resume = own.context.resume();
      if (!own.loading) own.loading = Promise.allSettled((['waves', 'gulls', 'piano'] as const).map(async name => {
        if (own.buffers[name]) return;
        const response = await fetch(`/audio/${name}.wav`);
        if (!response.ok) throw new Error(name);
        own.buffers[name] = await own.context.decodeAudioData(await response.arrayBuffer());
      })).then(results => {
        if (engine.current !== own) return;
        if (results.some(result => result.status === 'rejected')) setMessage('일부 소리를 불러오지 못했어요. 소리 다시 재생을 눌러 주세요.');
        else setMessage(null);
      }).finally(() => { own.loading = null; });
      void Promise.all([resume, own.loading]).then(() => {
        if (engine.current !== own || !own.wants || own.context.state === 'closed') return;
        if (document.hidden) { void own.context.suspend(); return; }
        for (const [name, gain] of [['waves', .48], ['piano', .3]] as const) {
          const buffer = own.buffers[name];
          if (!buffer || own.loops.has(name)) continue;
          const source = own.context.createBufferSource(), level = own.context.createGain();
          source.buffer = buffer; source.loop = true; level.gain.value = gain;
          source.connect(level).connect(own.master); source.start(); own.loops.set(name, source);
        }
        const gull = () => {
          own.timer = null;
          if (!own.wants || own.context.state === 'closed') return;
          if (own.context.state === 'running' && !document.hidden && own.buffers.gulls) {
            const source = own.context.createBufferSource(), level = own.context.createGain();
            source.buffer = own.buffers.gulls; level.gain.value = .12;
            source.connect(level).connect(own.master); source.start();
            source.onended = () => { source.disconnect(); level.disconnect(); };
          }
          own.timer = setTimeout(gull, 18000 + Math.random() * 16000);
        };
        if (!own.timer && own.buffers.gulls) own.timer = setTimeout(gull, 4500);
        applyVolume(); setPlaying(own.context.state === 'running' && own.loops.size > 0);
      }).catch(() => { if (engine.current === own) { setPlaying(false); setMessage('소리 켜기를 눌러 해변의 소리를 재생해 주세요.'); } });
    } catch {
      setPlaying(false); setMessage('이 브라우저에서는 소리를 재생할 수 없어요. 해변은 그대로 즐길 수 있어요.');
    }
  }, [applyVolume]);

  const stop = useCallback(() => {
    const e = engine.current;
    if (e) { e.wants = false; if (e.timer) { clearTimeout(e.timer); e.timer = null; } if (e.context.state === 'running') void e.context.suspend(); }
    setPlaying(false);
  }, []);

  const toggleMuted = useCallback(() => {
    const next = !settings.current.muted; settings.current.muted = next; setMuted(next); applyVolume();
    if (!next) start();
  }, [applyVolume, start]);

  const setVolume = useCallback((v: number) => {
    if (!Number.isFinite(v)) return;
    settings.current.volume = Math.min(1, Math.max(0, v)); setVolumeState(settings.current.volume); applyVolume();
  }, [applyVolume]);

  useEffect(() => {
    const visibility = () => {
      const e = engine.current; if (!e) return;
      if (document.hidden) {
        if (e.timer) { clearTimeout(e.timer); e.timer = null; }
        void e.context.suspend().then(() => setPlaying(false)).catch(() => {});
      } else if (e.wants && !settings.current.muted) start();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      const e = engine.current; engine.current = null;
      if (e) { e.wants = false; if (e.timer) clearTimeout(e.timer); for (const source of e.loops.values()) source.stop(); void e.context.close().catch(() => {}); }
    };
  }, [start]);

  return { playing, muted, volume, message, start, stop, toggleMuted, setVolume };
}
